"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.toDatePickerDate = exports.toDate = exports.isValidDate = exports.isPartialRange = exports.isFullRange = exports.isDayDisabled = exports.getVisibleDateFromProps = exports.getToday = exports.getSinglePickerDayLabel = exports.getRangePickerDayLabel = exports.getMonthMatrix = exports.formatDayLabel = exports.clampDate = void 0;
var _clamp = _interopRequireDefault(require("date-fns/clamp"));
var _eachDayOfInterval = _interopRequireDefault(require("date-fns/eachDayOfInterval"));
var _eachWeekOfInterval = _interopRequireDefault(require("date-fns/eachWeekOfInterval"));
var _endOfMonth = _interopRequireDefault(require("date-fns/endOfMonth"));
var _endOfWeek = _interopRequireDefault(require("date-fns/endOfWeek"));
var _isAfter = _interopRequireDefault(require("date-fns/isAfter"));
var _isBefore = _interopRequireDefault(require("date-fns/isBefore"));
var _isToday = _interopRequireDefault(require("date-fns/isToday"));
var _isValid = _interopRequireDefault(require("date-fns/isValid"));
var _startOfMonth = _interopRequireDefault(require("date-fns/startOfMonth"));
var _startOfWeek = _interopRequireDefault(require("date-fns/startOfWeek"));
/**
 * Adapted from https://gist.github.com/miljan-aleksic/bd70452a3f0cd6a11545db9f6ab57df6
 */
const getMonthMatrix = date => {
  const matrix = (0, _eachWeekOfInterval.default)({
    start: (0, _startOfMonth.default)(date),
    end: (0, _endOfMonth.default)(date)
  });
  return matrix.flatMap(weekDay => (0, _eachDayOfInterval.default)({
    start: (0, _startOfWeek.default)(weekDay),
    end: (0, _endOfWeek.default)(weekDay)
  }));
};

/**
 * @returns today's date, in the current time zone, stripped of any hours, minutes, seconds,
 * or sub-seconds.
 */
exports.getMonthMatrix = getMonthMatrix;
const getToday = () => {
  const today = new Date();
  return new Date(today.getFullYear(), today.getMonth(), today.getDate());
};

/**
 * @returns A javascript Date object for the given DatePickerDate object. This will be in the
 * current time zone, stripped of any hours, minutes, seconds, or sub-seconds.
 */
exports.getToday = getToday;
const toDate = pickerDate => new Date(pickerDate.year, pickerDate.month, pickerDate.day);

/**
 * @returns a DatePickerDate object from the given javascript Date object.
 */
exports.toDate = toDate;
const toDatePickerDate = date => ({
  year: date.getFullYear(),
  month: date.getMonth(),
  day: date.getDate()
});
exports.toDatePickerDate = toDatePickerDate;
const pad = num => {
  let str = String(num);
  let i = -1;
  const length = 2 - str.length;
  while (++i < length) {
    str = "0" + str;
  }
  return str;
};
const isValidDate = date => {
  if (!date) {
    return false;
  }

  // Add 1 to month here for the ISO date format constructor call.
  const parsed = new Date(`${date.year}-${pad(date.month + 1)}-${pad(date.day)}T00:00:00Z`);
  return (0, _isValid.default)(parsed) &&
  // Ensure there is no overflow on the month or day.
  date.day === parsed.getUTCDate() && date.month === parsed.getUTCMonth() && date.year === parsed.getUTCFullYear();
};
exports.isValidDate = isValidDate;
const isDayDisabled = (day, dayIsDisabled, minDate, maxDate) => {
  if (minDate && (0, _isBefore.default)(day, toDate(minDate))) {
    return true;
  }
  if (maxDate && (0, _isAfter.default)(day, toDate(maxDate))) {
    return true;
  }
  return dayIsDisabled?.(toDatePickerDate(day)) ?? false;
};
exports.isDayDisabled = isDayDisabled;
const isPartialRange = range => Array.isArray(range) && range[0] !== null && range[1] === null;
exports.isPartialRange = isPartialRange;
const isDatePickerDate = date => {
  return date && Object.hasOwnProperty.call(date, "year");
};
const isFullRange = range => Array.isArray(range) && range[0] !== null && range[1] !== null;
exports.isFullRange = isFullRange;
const formatDayLabel = (locale, date) => {
  return (isDatePickerDate(date) ? toDate(date) : date).toLocaleString(locale, {
    weekday: "long",
    day: "numeric",
    month: "long"
  });
};
exports.formatDayLabel = formatDayLabel;
const getSinglePickerDayLabel = (i18n, locale, day) => {
  if ((0, _isToday.default)(day)) {
    return i18n._( /*i18n*/{
      id: "Today, {dateStr}",
      values: {
        dateStr: formatDayLabel(locale, day)
      },
      comment: "This is the alt label for the button on the date picker for today's date"
    });
  } else {
    return formatDayLabel(locale, day);
  }
};
exports.getSinglePickerDayLabel = getSinglePickerDayLabel;
const getRangePickerDayLabel = (i18n, locale, day, range) => {
  if (range === null || isFullRange(range)) {
    return i18n._( /*i18n*/{
      id: "{dateStr}, select start date",
      values: {
        dateStr: formatDayLabel(locale, day)
      },
      comment: "This is the alt text in the date range picker for any date button before a start date has been chosen. " + "The comma here is important as it provides a little pause for a screen reader"
    });
  } else {
    return i18n._( /*i18n*/{
      id: "{dateStr}, select end date",
      values: {
        dateStr: formatDayLabel(locale, day)
      },
      comment: "This is the alt text in the date range picker for any date button after a start date has been chosen. " + "The comma here is important as it provides a little pause for a screen reader"
    });
  }
};

/**
 * @returns the visible date to use for the date picker. This will be the provided date if it's
 * valid, the initialPickerDate if it's valid, or today's date if neither of those are valid. This
 * is used to determine the initial visible date for the date picker from the props provided. It
 * also clamps the returned date to be within the min/max dates.
 */
exports.getRangePickerDayLabel = getRangePickerDayLabel;
const getVisibleDateFromProps = (value, initialPickerDate, minDate, maxDate) => {
  let dateToUse = null;
  if (value !== null && isValidDate(value)) {
    // If there's a selected value, use that.
    dateToUse = value;
  } else if (initialPickerDate) {
    // If there's an initialPickerDate, use that.
    dateToUse = initialPickerDate;
  } else {
    // otherwise, use today.
    dateToUse = toDatePickerDate(getToday());
  }
  // Always clamp this selection to be within the min/max dates.
  const clamped = clampDate(dateToUse, minDate, maxDate);
  return clamped;
};
exports.getVisibleDateFromProps = getVisibleDateFromProps;
const MIN_DATE = new Date(-8640000000000000);
const MAX_DATE = new Date(8640000000000000);
const clampDate = (date, minDate, maxDate) => (0, _clamp.default)(toDate(date), {
  start: minDate ? toDate(minDate) : MIN_DATE,
  end: maxDate ? toDate(maxDate) : MAX_DATE
});
exports.clampDate = clampDate;
//# sourceMappingURL=calendar.js.map